import { useState } from "react";
import type { AlteracaoDePlano, ContratoDoPlano, Plano } from "@/api/contrato";
import { useSessao } from "@/sessao/useSessao";
import { useAlterarPlano } from "@/api/consultas";
import { textoDoErro } from "@/api/problema";
import { ModalDeCadastro } from "@/componentes/ModalDeCadastro";
import { Confirmacao } from "@/componentes/Confirmacao";
import { useAvisos } from "@/componentes/Avisos";
import { contagem } from "@/formato";
import { FormularioDePlano } from "./FormularioDePlano";
import { nomeDoContratante } from "./situacao";
import {
  campoRecusado,
  confirmacaoNecessaria,
  corpoDaEdicao,
  planoFaturado,
  mudaCampoComercial,
  validarPlano,
  valoresDe,
  type CampoDoPlano,
  type ErrosDoPlano,
  type ValoresDoPlano,
} from "./plano";

interface Afetados {
  corpo: AlteracaoDePlano;
  contagem: number;
  /** A marca do CONJUNTO que o operador está vendo, e que a confirmação repete. */
  marca: string;
  aplicacoes: string[];
}

function nomesVigentes(contratos: readonly ContratoDoPlano[]): string[] {
  return contratos.filter((contrato) => contrato.situacao === "vigente").map(nomeDoContratante);
}

/*
 * Edição de plano, com a confirmação dos contratos afetados.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ A ALTERAÇÃO VALE PARA O MÊS INTEIRO, E O SERVIDOR CONFERE A CONTAGEM.    │
 * │                                                                          │
 * │ A quota é por competência: baixar a franquia hoje reclassifica como      │
 * │ excedente o que foi emitido no dia 3. Por isso campo comercial de plano  │
 * │ com contrato vigente exige confirmação — e a confirmação manda a MARCA   │
 * │ do conjunto que o operador viu, e não só a contagem: sair um contrato e  │
 * │ entrar outro mantém o número. Se o conjunto mudou nesse meio tempo, o    │
 * │ servidor responde `409 confirmacao_necessaria` com a marca nova, e a     │
 * │ tela pergunta de novo, com os nomes novos. Confirmar um conjunto velho   │
 * │ seria aprovar uma mudança sobre um cliente que ninguém viu na lista.     │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
export function EditarPlano({
  plano,
  contratos,
  aoFechar,
}: {
  plano: Plano;
  contratos: readonly ContratoDoPlano[];
  aoFechar: () => void;
}) {
  const { cliente } = useSessao();
  const avisos = useAvisos();
  const alterar = useAlterarPlano(cliente, plano.id);

  const [valores, setValores] = useState<ValoresDoPlano>(() => valoresDe(plano));
  const [erros, setErros] = useState<ErrosDoPlano>({});
  const [erroGeral, setErroGeral] = useState<string | undefined>(undefined);
  const [afetados, setAfetados] = useState<Afetados | null>(null);

  function mudar(campo: CampoDoPlano, valor: string) {
    setValores((atuais) => ({ ...atuais, [campo]: valor }));
    setErros((atuais) => ({ ...atuais, [campo]: undefined }));
  }

  /**
   * Grava. Devolve `false` quando o servidor pediu (outra) confirmação — aí a
   * tela abre o diálogo com o conjunto dele, e não com o nosso.
   */
  async function gravar(corpo: AlteracaoDePlano, marca?: string): Promise<boolean> {
    try {
      await alterar.mutateAsync(marca === undefined ? corpo : { ...corpo, marca_dos_vigentes: marca });
    } catch (falha) {
      const pedido = confirmacaoNecessaria(falha);
      if (pedido !== null) {
        setAfetados({
          corpo,
          contagem: pedido.contratos_vigentes,
          marca: pedido.marca_dos_vigentes,
          aplicacoes: nomesVigentes(pedido.contratos),
        });
        return false;
      }
      /*
       * Plano já faturado não tem confirmação que o libere: a recusa vai para o
       * formulário, onde o operador vê a orientação (plano novo e troca dos
       * contratos) ao lado do campo que tentou mudar — e não num diálogo de
       * confirmação que só teria o botão de voltar.
       */
      if (planoFaturado(falha)) {
        setAfetados(null);
        setErroGeral(textoDoErro(falha));
        return false;
      }
      throw falha;
    }

    avisos.feito("Plano alterado");
    aoFechar();
    return true;
  }

  function enviar() {
    setErroGeral(undefined);
    const validacao = validarPlano(valores);
    if (!validacao.ok) {
      setErros(validacao.erros);
      return;
    }

    const corpo = corpoDaEdicao(plano, validacao.plano);
    if (Object.keys(corpo).length === 0) {
      aoFechar();
      return;
    }

    if (mudaCampoComercial(corpo) && plano.contratos_vigentes > 0) {
      setAfetados({
        corpo,
        contagem: plano.contratos_vigentes,
        marca: plano.marca_dos_vigentes,
        aplicacoes: nomesVigentes(contratos),
      });
      return;
    }

    gravar(corpo).catch((falha: unknown) => {
      const campo = campoRecusado(falha);
      if (campo !== null) setErros({ [campo]: textoDoErro(falha) });
      else setErroGeral(textoDoErro(falha));
    });
  }

  return (
    <>
      <ModalDeCadastro
        aberto
        aoFechar={aoFechar}
        titulo="Editar plano"
        descricao={plano.nome}
        aoEnviar={enviar}
        enviando={alterar.isPending}
        erro={erroGeral}
        rotuloDeEnvio="Salvar"
      >
        <FormularioDePlano valores={valores} erros={erros} aoMudar={mudar} />
      </ModalDeCadastro>

      <Confirmacao
        pedido={
          afetados === null
            ? null
            : {
                severidade: "destrutiva",
                // A marca remonta o diálogo quando o servidor devolve outro
                // conjunto — inclusive com a mesma contagem: a pergunta nova
                // não herda o estado (nem o botão travado) da velha.
                identidade: afetados.marca,
                titulo: `Alterar ${contagem(afetados.contagem, "contrato vigente", "contratos vigentes")}?`,
                corpo: `Vale para o mês inteiro de: ${afetados.aplicacoes.join(", ") || "—"}.`,
                verbo: "Alterar plano",
              }
        }
        aoCancelar={() => setAfetados(null)}
        aoConfirmar={async () => {
          if (afetados === null) return;
          const gravou = await gravar(afetados.corpo, afetados.marca);
          if (gravou) setAfetados(null);
        }}
      />
    </>
  );
}
